import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  DeleteEmailTransmissionApplicationErrors,
  EmailTransmission,
  EmailTransmissionCreatedEnvelope,
  EmailTransmissionDeletedEnvelope,
  EmailTransmissionQuery,
  EmailTransmissionUpdatedEnvelope,
  EmailTransmissionsQueryBuilder,
  QueryEmailTransmissionsResponse,
  SendEmailTransmissionApplicationErrors,
  SendEmailTransmissionOptions,
  SendEmailTransmissionResponse,
  SendEmailTransmissionValidationErrors,
  deleteEmailTransmission as universalDeleteEmailTransmission,
  getEmailTransmission as universalGetEmailTransmission,
  queryEmailTransmissions as universalQueryEmailTransmissions,
  sendEmailTransmission as universalSendEmailTransmission,
  typedQueryEmailTransmissions as universalTypedQueryEmailTransmissions,
} from './emails-transmissions-v1-email-transmission-email-transmissions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/email-transmissions' };

export function sendEmailTransmission(
  httpClient: HttpClient
): SendEmailTransmissionSignature {
  return (
    emailTransmission: NonNullablePaths<
      EmailTransmission,
      `emailHtmlContent` | `emailSubject` | `toRecipients` | `type`,
      2
    >,
    options?: SendEmailTransmissionOptions
  ) =>
    universalSendEmailTransmission(
      emailTransmission,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendEmailTransmissionSignature {
  /**
   * Creates and sends an email transmission to one or more recipients.
   *
   * The email is sent asynchronously. The returned transmission has `ACCEPTED` status.
   * Call [Get Email Transmission](https://dev.wix.com/docs/api-reference/business-management/marketing/emails/email-transmission-v1/get-email-transmission) or listen for the `EmailTransmissionCreated` webhook to track delivery status.
   * @param - Email transmission to send.
   */
  (
    emailTransmission: NonNullablePaths<
      EmailTransmission,
      `emailHtmlContent` | `emailSubject` | `toRecipients` | `type`,
      2
    >,
    options?: SendEmailTransmissionOptions
  ): Promise<
    NonNullablePaths<
      SendEmailTransmissionResponse,
      | `emailTransmission.rejectedOptions.rejectionReasons`
      | `emailTransmission._id`
      | `emailTransmission.emailSubject`
      | `emailTransmission.emailHtmlContent`
      | `emailTransmission.replyTo.contactId`
      | `emailTransmission.replyTo.userId`
      | `emailTransmission.replyTo.emailAddress`
      | `emailTransmission.replyTo.status`
      | `emailTransmission.replyTo.failureReason`
      | `emailTransmission.toRecipients`
      | `emailTransmission.type`
      | `emailTransmission.status`,
      4
    > & {
      __applicationErrorsType?: SendEmailTransmissionApplicationErrors;
      __validationErrorsType?: SendEmailTransmissionValidationErrors;
    }
  >;
}

export function getEmailTransmission(
  httpClient: HttpClient
): GetEmailTransmissionSignature {
  return (emailTransmissionId: string) =>
    universalGetEmailTransmission(
      emailTransmissionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEmailTransmissionSignature {
  /**
   * Retrieves an email transmission by ID.
   * @param - Email transmission ID.
   * @returns Retrieved email transmission.
   */
  (emailTransmissionId: string): Promise<
    NonNullablePaths<
      EmailTransmission,
      | `rejectedOptions.rejectionReasons`
      | `_id`
      | `emailSubject`
      | `emailHtmlContent`
      | `replyTo.contactId`
      | `replyTo.userId`
      | `replyTo.emailAddress`
      | `replyTo.status`
      | `replyTo.failureReason`
      | `toRecipients`
      | `type`
      | `status`,
      3
    >
  >;
}

export function queryEmailTransmissions(
  httpClient: HttpClient
): QueryEmailTransmissionsSignature {
  return () =>
    universalQueryEmailTransmissions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryEmailTransmissionsSignature {
  /**
   * Retrieves a list of up to 100 email transmissions, given the provided paging, filtering, and sorting.
   *
   * `emailHtmlContent` is not returned in the results. To retrieve the full HTML content of a specific transmission,
   * call [Get Email Transmission](https://dev.wix.com/docs/api-reference/business-management/marketing/emails/email-transmission-v1/get-email-transmission).
   *
   * Query Email Transmissions runs with these defaults, which you can override:
   *
   * - `updatedDate` is sorted in `DESC` order
   * - `paging.limit` is `50`
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
   * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging),
   * and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   */
  (): EmailTransmissionsQueryBuilder;
}

export function typedQueryEmailTransmissions(
  httpClient: HttpClient
): TypedQueryEmailTransmissionsSignature {
  return (query: EmailTransmissionQuery) =>
    universalTypedQueryEmailTransmissions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryEmailTransmissionsSignature {
  /** */
  (query: EmailTransmissionQuery): Promise<
    NonNullablePaths<
      QueryEmailTransmissionsResponse,
      | `emailTransmissions`
      | `emailTransmissions.${number}._id`
      | `emailTransmissions.${number}.emailSubject`
      | `emailTransmissions.${number}.emailHtmlContent`
      | `emailTransmissions.${number}.replyTo.contactId`
      | `emailTransmissions.${number}.replyTo.userId`
      | `emailTransmissions.${number}.replyTo.emailAddress`
      | `emailTransmissions.${number}.replyTo.status`
      | `emailTransmissions.${number}.replyTo.failureReason`
      | `emailTransmissions.${number}.type`
      | `emailTransmissions.${number}.status`,
      5
    >
  >;
}

export function deleteEmailTransmission(
  httpClient: HttpClient
): DeleteEmailTransmissionSignature {
  return (emailTransmissionId: string) =>
    universalDeleteEmailTransmission(
      emailTransmissionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteEmailTransmissionSignature {
  /**
   * Deletes an email transmission by ID.
   *
   * Deletion is only allowed for transmissions with `PROCESSED` or `REJECTED` status.
   * @param - Email transmission ID.
   */
  (emailTransmissionId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteEmailTransmissionApplicationErrors;
    }
  >;
}

export const onEmailTransmissionCreated = /*#__PURE__*/ EventDefinition(
  'wix.emails.transmissions.v1.email_transmission_created',
  true,
  (event: EmailTransmissionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EmailTransmissionCreatedEnvelope>();
export const onEmailTransmissionDeleted = /*#__PURE__*/ EventDefinition(
  'wix.emails.transmissions.v1.email_transmission_deleted',
  true,
  (event: EmailTransmissionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EmailTransmissionDeletedEnvelope>();
export const onEmailTransmissionUpdated = /*#__PURE__*/ EventDefinition(
  'wix.emails.transmissions.v1.email_transmission_updated',
  true,
  (event: EmailTransmissionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<EmailTransmissionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  AccountSuspendedErrorDetails,
  ActionEvent,
  BaseEventMetadata,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteEmailTransmissionRequest,
  DeleteEmailTransmissionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EmailTransmission,
  EmailTransmissionCreatedEnvelope,
  EmailTransmissionDeletedEnvelope,
  EmailTransmissionQuerySpec,
  EmailTransmissionStatusOptionsOneOf,
  EmailTransmissionTypeOptionsOneOf,
  EmailTransmissionUpdatedEnvelope,
  EmailTransmissionsQueryBuilder,
  EmailTransmissionsQueryResult,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FailureReason,
  GetDeletedEmailTransmissionRequest,
  GetDeletedEmailTransmissionResponse,
  GetEmailTransmissionRequest,
  GetEmailTransmissionResponse,
  GetLiveEmailTransmissionRequest,
  GetLiveEmailTransmissionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MarketingOptions,
  MessageEnvelope,
  OutOfQuotaErrorDetails,
  QueryDeletedEmailTransmissionsRequest,
  QueryDeletedEmailTransmissionsResponse,
  QueryEmailTransmissionsRequest,
  QueryEmailTransmissionsResponse,
  QueryLiveEmailTransmissionsRequest,
  QueryLiveEmailTransmissionsResponse,
  Recipient,
  RecipientStatus,
  RecipientValueOneOf,
  RejectedOptions,
  RejectionReason,
  RestoreInfo,
  SendEmailTransmissionOptions,
  SendEmailTransmissionRequest,
  SendEmailTransmissionResponse,
  SortOrder,
  Sorting,
  Status,
  Type,
  WebhookIdentityType,
  utils,
} from './emails-transmissions-v1-email-transmission-email-transmissions.universal.js';
